import { apiClient } from '@/services/api'
import endpoints from '@/services/endpoints'
import { objectHasPropertyValue } from 'Utils'
import { checkErrorResponse } from 'Services/helpers'
import { Notification } from 'Utils/Classes/Notification'

// Preparing notification error
const titleError = new Error('El título no puede estar vacío')
titleError.name = 'empty-title'
const notification = new Notification()

export default {
  // TEMPLATE ACTIONS
  get: {
    templates: () => apiClient.get(endpoints.templates.store()),
    templatePublished: (id) => apiClient.get(endpoints.templates.template(id)),
    templateRecentDraft: (id) => apiClient.get(endpoints.templates.templateEdit(id)),
    templateTranslations: (id) => apiClient.get(endpoints.templates.templateTranslations(id)),
  },

  add: {
    /**
     * Add template
     *
     * @param {Object} templateData - The new template's basic information.
     * @param {String} templateData.title - The new template's title.
     * @param {String|Number} templateData.category - The new template's category.
     * @returns {Object} - success: new template's data or error api response.
     */
    async template(templateData) {
      try {
        const newTemplate = await apiClient.post(endpoints.templates.store(), templateData)
        notification.type = 'success'
        notification.timeout = 4000
        notification.message = 'Plantilla creada con éxito'
        return newTemplate
      } catch (error) {
        notification.type = 'error'
        notification.message = `No se ha podido crear la plantilla: parece que hay problemas de conexión`
        return error
      } finally {
        notification.dispatch()
      }
    },

    /**
     * Add template translation
     *
     * @param {Number|String} id - The template's id to translate.
     * @param {String} languageCode - Translation's language code, ej: 'es'.
     *
     * @returns {Object} - success: new template's translation data or error api response.
     */
    async templateTranslation(id, languageCode) {
      try {
        const newTemplate = await apiClient.post(
          endpoints.templates.templateTranslations(id),
          languageCode
        )
        notification.type = 'success'
        notification.timeout = 4000
        notification.message = 'Traducción creada con éxito'
        return newTemplate
      } catch (error) {
        notification.type = 'error'
        if (error.response && error.response.status === 404) {
          notification.message = `No se ha podido crear la traducción: la plantilla no existe`
        } else {
          notification.message = `Ocurrió un error al intentar crear la traducción`
        }
        return error
      } finally {
        notification.dispatch()
      }
    },
  },

  update: {
    /**
     * Update - template
     *
     * @param {String|Number} id - The template's id to update.
     * @param {Object} newValue - The template's values to update, ej: { title: 'New title' }.
     *
     * @returns {Object} - success: updated template's data or error api response.
     */
    async template(id, newValue) {
      try {
        const update = await apiClient.patch(endpoints.templates.template(id), newValue)
        return update
      } catch (error) {
        notification.type = 'error'
        notification.message = `Error al actualizar la plantilla: ${checkErrorResponse(error)}`
        notification.dispatch()
        return error
      }
    },

    /**
     * Update - template draft
     *
     * @param {String|Number} id - Template's draft id to update.
     * @param {Object} newValue - Template's draft values to update, ej: { title: 'New title' }.
     *
     * @returns {Object} - success: updated template's draft data or error api response.
     */
    async templateDraft(id, newValue) {
      try {
        const update = await apiClient.patch(endpoints.templates.templateEdit(id), newValue)
        return update
      } catch (error) {
        notification.type = 'error'
        if (error.response) {
          notification.message = `${error.response.data.error}`
        } else {
          notification.message = `Error al actualizar la plantilla: ${checkErrorResponse(error)}`
        }
        notification.dispatch()
        return error
      }
    },
  },

  // TEMPLATE's CHECKLISTS ACTIONS
  checklists: {
    /**
     *  Create
     *
     * @param {Nuber | String} templateId The template that will hold the new checklist.
     * @param {Object} checklistInfo Checklist's required initial info.
     * @param {String} checklistInfo.title Checklist's title ( must be in spanish).
     * @param {Number} checklistInfo.order Checklist's position.
     * @returns {Object} - success: new checklist data or error api response.
     */
    async create(templateId, checklistInfo) {
      try {
        if (!objectHasPropertyValue(checklistInfo, 'title')) throw titleError
        const newChecklist = await apiClient.post(
          endpoints.templates.templateChecklists(templateId),
          checklistInfo
        )
        return newChecklist
      } catch (error) {
        // 'title' field can't be empty
        notification.type = 'error'
        if (error.name === 'empty-title') {
          notification.message = error.message
        } else {
          notification.message = `No se ha podido crear el checklist: ${checkErrorResponse(error)}`
        }
        notification.dispatch()
        return error
      }
    },

    /**
     * Update
     * @param {String | Number} templateId Template that holds the checklist.
     * @param {String | Number} checklistId Checklist to update.
     * @param {Object} updatedFields Fields to update.
     * @param {String} [updatedFields.title] Optional: Spanish checklist's title.
     * @param {String} [updatedFields.description] Optional: Spanish checklist's description.
     * @param {Number} [updatedFields.order] Optional: Checklist's order.
     * @returns {Object} updated checklist data or error api response.
     */
    async update(templateId, checklistId, updatedFields) {
      try {
        if (!objectHasPropertyValue(updatedFields, 'title')) throw titleError
        const updatedChecklist = await apiClient.patch(
          endpoints.templates.templateChecklist(templateId, checklistId),
          updatedFields
        )
        return updatedChecklist
      } catch (error) {
        notification.type = 'error'
        // 'title' field can't be empty
        if (error.name === 'empty-title') {
          notification.message = error.message
        } else {
          notification.message = `No se ha podido actualizar el checklist: ${checkErrorResponse(
            error
          )}`
        }
        notification.dispatch()
        return error
      }
    },

    /**
     * Delete
     *
     * @param {String | Number} templateId Template that holds the checklist.
     * @param {String | Number} checklistId Checklist to delete.
     * @returns error api response if fails.
     */
    async delete(templateId, checklistId) {
      try {
        return await apiClient.delete(
          endpoints.templates.templateChecklist(templateId, checklistId)
        )
      } catch (error) {
        notification.type = 'error'
        notification.message = `No se ha podido eliminar el checklist: ${checkErrorResponse(error)}`
        notification.dispatch()
        return error
      }
    },

    // ITEMS ACTIONS
    items: {
      /**
       *  Create
       *
       * @param {Nuber | String} templateId The template that hold's the new item's checklist.
       * @param {Nuber | String} checklistId The new item's checklist.
       * @param {Object} newItemInfo The Item's required initial info.
       * @param {String} newItemInfo.title Item's spanish title.
       * @param {Number} newItemInfo.order Item's position.
       * @returns {Object} newItem or error api response.
       */
      async create(templateId, checklistId, newData) {
        try {
          if (!objectHasPropertyValue(newData, 'title')) throw titleError
          const newItem = await apiClient.post(
            endpoints.templates.templateChecklistItems(templateId, checklistId),
            newData
          )
          return newItem
        } catch (error) {
          // 'title' field can't be empty
          notification.type = 'error'
          if (error.name === 'empty-title') {
            notification.message = `El título no puede estar vacío`
          } else {
            notification.message = `No se ha podido crear la tarea: ${error.message}`
          }
          notification.dispatch()
          return error
        }
      },

      /**
       * Update
       *
       * @param {String | Number} templateId The template that holds the item's checklist.
       * @param {String | Number} checklistId The item's checklist.
       * @param {String | Number} itemId Item to update.
       * @param {Object} updatedValues Values to update.
       * @param {String} [updatedValues.title] Item's initial title.
       * @param {String} [updatedValues.type] Item's type: 'client', 'diy'.
       * @param {Number} [updatedValues.order] Item's position.
       * @returns {Object} updatesValues or error response.
       */
      async update({ cardId, checklistId, itemId, newData }) {
        // const title = Object.keys(newData).filter((key) => !key.indexOf('title'))[0]
        try {
          if (!objectHasPropertyValue(newData, 'title')) throw titleError
          const updatedItem = await apiClient.patch(
            endpoints.templates.templateChecklistItem(cardId, checklistId, itemId),
            newData
          )
          return updatedItem
        } catch (error) {
          notification.type = 'error'
          // 'title' field can't be empty
          if (error.name === 'empty-title') {
            notification.message = error.message
          } else {
            notification.message = `No se ha podido actualizar la tarea: ${checkErrorResponse(
              error
            )}`
          }
          notification.dispatch()
          return error
        }
      },

      /**
       * Delete
       *
       * @param {String | Number} templateId Template that holds the checklist.
       * @param {String | Number} checklistId Item's checklist id.
       * @param {String | Number} itemId Item to delete.
       * @returns error api response if fails.
       */
      async delete(templateId, checklistId, itemId) {
        try {
          await apiClient.delete(
            endpoints.templates.templateChecklistItem(templateId, checklistId, itemId)
          )
        } catch (error) {
          notification.type = 'error'
          notification.message = `No se ha podido eliminar el item: ${checkErrorResponse(error)}`
          notification.dispatch()
          return error
        }
      },
    },
  },
}
